import {
  HttpLink,
  ApolloClient,
  InMemoryCache,
  from,
  createQueryPreloader,
  InMemoryCacheConfig,
   split
} from '@apollo/client';
import { setContext } from "@apollo/client/link/context";
import { onError, ErrorResponse } from "@apollo/client/link/error";
import { errorTracker, graphqlErrorHandler } from './graphql-error-handler';
import { environment } from '@coverright/shared/config/environment';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';

export { GraphQLErrorType } from './graphql-error-type';

export const getApolloClient = (
  getTokenFunc: () => Promise<string | undefined> = async () => undefined,
  uri?: string,
  inMemoryCacheConfig: InMemoryCacheConfig = {},
  useWebSocket = false,
  onAuthFailure?: () => void,
) => {
  const httpLink = new HttpLink({
    uri: uri || environment.graphqlUrl,
    headers: {
      "content-type": "application/json",
    }
  });

  const authLink = setContext(async (_, { headers }: any) => {
    let token: string | undefined;
    try {
      token = await getTokenFunc();
    } catch (e) {
      token = undefined;
      onAuthFailure?.();
    }
    if (token) {
      return {
        headers: {
          authorization: token ? `Bearer ${token}` : "",
          ...headers,
        }
      };
    } else {
      return {
        headers
      };
    }
  });

  const wsLink = new GraphQLWsLink(createClient({
    url: environment.enrollSubscriptionsUrl,
    connectionParams: async () => {
      const token = await getTokenFunc();
      return {
        Authorization: token ? token : "",
      }
    },
    lazy: true,
  }));

  const elink = onError((errResponse: ErrorResponse) => {
    try {
      getTokenFunc().then(token => errorTracker(errResponse, token)).catch(() => errorTracker(errResponse));
    } catch (e) {
      errorTracker(errResponse);
    }
    if (graphqlErrorHandler) {
      graphqlErrorHandler(errResponse);
    }
  });

  const requestLink =
    from([
      authLink,
      elink,
      httpLink,
    ])
  ;

  const link = useWebSocket ?
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      requestLink
    ):
    requestLink
  ;

  const cache = new InMemoryCache(inMemoryCacheConfig);

  const clientInstance = new ApolloClient({
    link,
    cache,
    connectToDevTools: true
  });
  const preloadQuery = createQueryPreloader(clientInstance);

  return { client: clientInstance, preloadQuery, cache };
}
